// @flow

import * as React from 'react';
import * as Types from '@ta/trips.trip-types';
import type { TripsError, TripsScopedError } from '@ta/trips.trip-errors';
import { AddItemWithFullTrip, RemoveItemWithFullTrip } from '@ta/trips.graphql';
import { useMutation } from 'urql';

export type ToggleState = {|
  fetching: boolean,
  saved: boolean,
  result?: {
    trip: Types.Trip,
    tripItem?: ?Types.TripItem,
  },
  error?: ?TripsScopedError,
|};

export type ToggleFunction = () => Promise<ToggleState>;

/**
 * A hook that toggles saving/removing an item from a given trip.
 *
 * @param object Object which will be saved to a trip
 * @param tripId The trip which this item will be saved/unsaved
 * @param tripItem The TripItem if this result has already been saved to the current trip. If provided, this component will remove the item from the trip on click, else the item
 * will be added to the trip on click.
 */
const useTripSaveToggle = (
  object: Types.TripSaveObject,
  tripId: number,
  tripItem?: ?Types.TripItem,
): [ToggleState, ToggleFunction] => {
  const [state, setState] = React.useState<ToggleState>({ fetching: false, saved: !!tripItem });
  const [, save] = useMutation(AddItemWithFullTrip);
  const [, remove] = useMutation(RemoveItemWithFullTrip);

  const removeItem = (): Promise<*> => {
    if (!tripItem) {
      return Promise.resolve(state);
    }
    setState({ ...state, fetching: true });
    return remove({
      itemId: tripItem.id,
      tripId,
    })
      .then(result => {
        /* $FlowFixGraphql (WP-1357) */
        const trip: ?Types.Trip = result?.data?.tripsRemoveItem;

        if (!trip) {
          setState({
            fetching: false,
            saved: true,
            error: { query: 'REMOVE_TRIP_ITEM', tripsError: undefined },
          });
          return state;
        }

        setState({
          fetching: false,
          saved: false,
          result: {
            trip,
          },
        });
        return state;
      })
      .catch(() => {
        setState({
          fetching: false,
          saved: true,
          error: { query: 'REMOVE_TRIP_ITEM' },
        });
        return state;
      });
  };

  const saveItem = () => {
    setState({ ...state, fetching: true });
    return save({
      tripId,
      referenceId: `${object.id}`,
      referenceType: object.type,
      allowDuplicates: false,
    })
      .then(result => {
        /* $FlowFixGraphql (WP-1357) */
        const trip: ?Types.Trip = result?.data?.addItemToTrip?.trip;
        /* $FlowFixGraphql (WP-1357) */
        const error: ?TripsError = result?.data?.addItemToTrip?.errors?.[0]?.error;

        if (error || !trip) {
          setState({
            fetching: false,
            saved: false,
            error: { query: 'ADD_ITEM_TO_TRIP', tripsError: error || undefined },
          });
          return state;
        }
        /* $FlowFixGraphql (WP-1357) */
        const item: ?Types.TripItem = result?.data?.addItemToTrip?.items?.[0];
        if (item) {
          setState({
            fetching: false,
            saved: true,
            result: {
              trip,
              tripItem: item,
            },
          });
        } else {
          setState({
            fetching: false,
            saved: false,
            error: { query: 'ADD_ITEM_TO_TRIP' },
          });
        }
        return state;
      })
      .catch(() => {
        setState({
          fetching: false,
          saved: false,
          error: { query: 'ADD_ITEM_TO_TRIP' },
        });
        return state;
      });
  };

  return [state, tripItem ? removeItem : saveItem];
};

export default useTripSaveToggle;
